// Implements: F-UIX-017, F-UIX-007
import { useLayoutEffect, useRef } from "react";
import type { ReactElement, RefObject } from "react";

import styles from "./Textarea.module.css";

export interface TextareaProps {
  readonly id: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  /** Ctrl+Enter or Cmd+Enter: the keyboard way to send. Enter alone writes a new line. */
  readonly onSubmitShortcut?: (() => void) | undefined;
  readonly invalid?: boolean | undefined;
  readonly describedBy?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly rows?: number | undefined;
  readonly placeholder?: string | undefined;
  /**
   * `bare`: no border, no focus ring of its own, for a field drawn inside a box that shows the
   * focus with `:focus-within` (the mockup's creation prompt and composer).
   */
  readonly variant?: "boxed" | "bare" | undefined;
  /** A handle on the element, for the caller that moves the focus to it (`autoFocus` is banned). */
  readonly inputRef?: RefObject<HTMLTextAreaElement | null> | undefined;
  /**
   * Grow with the content from `rows` lines up to a ceiling (CSS: 40 % of the viewport, about
   * twelve lines), then scroll inside, and shrink when the text goes (a deletion, or a reset
   * after a send). Opt-in: a field that is not asked to grow behaves exactly as before.
   */
  readonly autoGrow?: boolean | undefined;
}

/** The custom property the CSS reads for the height of a growing field (no style attribute: the CSP bans it). */
const GROW_PROPERTY = "--textarea-grow-height";

/** The send shortcut as ARIA names it: Ctrl+Enter, or Cmd+Enter on a Mac. */
const SUBMIT_SHORTCUT = "Control+Enter Meta+Enter";

/**
 * A controlled multi-line field, for text that is prose and not a single value.
 *
 * Like `Input`, it hands over the value and never the event, and its
 * accessibility comes from its props. The one thing it adds is the send
 * shortcut, because in a field where Enter means "new line" the keyboard needs
 * another way to send (F-UIX-017). The field names that shortcut with
 * `aria-keyshortcuts`, and only when it has one. A held key sends once: its repeats are ignored.
 */
export function Textarea({
  id,
  value,
  onChange,
  onSubmitShortcut,
  invalid = false,
  describedBy,
  disabled = false,
  rows = 3,
  placeholder,
  variant = "boxed",
  inputRef,
  autoGrow = false,
}: TextareaProps): ReactElement {
  const own = useRef<HTMLTextAreaElement | null>(null);
  const element = inputRef ?? own;
  // Every change of the text, typed, pasted or reset from outside, lands here: the previous height is
  // released first so the content, and not the old box, is what gets measured.
  useLayoutEffect(() => {
    const box = element.current;
    if (!autoGrow || box === null) return;
    box.style.removeProperty(GROW_PROPERTY);
    box.style.setProperty(GROW_PROPERTY, `${String(box.scrollHeight)}px`);
  }, [autoGrow, value, element]);

  return (
    <textarea
      id={id}
      ref={element}
      className={[variant === "bare" ? styles.bare : styles.textarea, autoGrow ? styles.grow : undefined].join(" ").trim()}
      placeholder={placeholder}
      value={value}
      rows={rows}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      aria-keyshortcuts={onSubmitShortcut === undefined ? undefined : SUBMIT_SHORTCUT}
      disabled={disabled}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      onKeyDown={(event) => {
        if (onSubmitShortcut === undefined) return;
        if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
          event.preventDefault();
          if (event.repeat) return;
          onSubmitShortcut();
        }
      }}
    />
  );
}
